import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

export interface UpcomingFixture {
  id: string;
  matchDate: string;
  opponent: string | null;
  isHome: boolean;
  competition: string | null;
  daysAway: number;
}

/**
 * Fetch the next upcoming fixture for the active tenant.
 * Returns `null` when no upcoming fixture exists.
 */
export function useNextFixture() {
  return useQuery<UpcomingFixture | null>({
    queryKey: ['next-fixture'],
    queryFn: async () => {
      const res = await api.get('/fixtures/upcoming', { params: { limit: 1 } });
      const list: UpcomingFixture[] = res.data?.fixtures ?? res.data ?? [];
      return list.length > 0 ? list[0] : null;
    },
    staleTime: 5 * 60_000, // fixtures don't change often
  });
}

/**
 * Convert `daysAway` into a microcycle label: MD-2, MD-1, MD, MD+1, MD+2 …
 */
export function daysAwayToMd(daysAway: number): string {
  if (daysAway === 0) return 'MD';
  if (daysAway > 0) return `MD-${daysAway}`;
  return `MD+${Math.abs(daysAway)}`;
}

/**
 * Human-readable timing: "Today", "Tomorrow", "In 3 days", "Yesterday", "2 days ago" …
 */
export function daysAwayToLabel(daysAway: number): string {
  if (daysAway === 0) return 'Today';
  if (daysAway === 1) return 'Tomorrow';
  if (daysAway > 1) return `In ${daysAway} days`;
  if (daysAway === -1) return 'Yesterday';
  return `${Math.abs(daysAway)} days ago`;
}
